import { Component, OnInit, Input, OnDestroy, ViewChild, Output, EventEmitter, Directive } from '@angular/core';
import { asyncScheduler, Subscription, timer } from 'rxjs';
import { TabDirective } from 'ngx-bootstrap/tabs';

import { ShowRequest } from '../../../core/show-request';
import { ScoutPath, MapImageOverlay, MapRouteGPXOverlay, MapDevice } from '../../../core/models';
import { TaskStatus } from '../../../core/models';
import { RoleMgr } from '../../../core';
import { BuildingService, PointService, BuildingDoc, ScoutTaskService, AntennaService } from '../../../core/services';
import { AppState } from '../../../core/app-state';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';
import { FesBaseComponent } from '../../../shared/components/fes-base.component';
import { ScoutPathPlotterComponent } from '../scout-path-plotter/scout-path-plotter.component';
import { ScoutSegmentsEditorComponent } from '../scout-segments-editor/scout-segments-editor.component';
import { ScoutPathCommentsComponent } from '../scout-path-comments/scout-path-comments.component';
import { AbstractControl, NgForm, NG_VALIDATORS, Validator, ValidatorFn } from '@angular/forms';
import { AppTab } from '../../../core/app-tab';
import { ScoutPathAzimuthComponent } from '../scout-path-azimuth/scout-path-azimuth.component';
import { Router } from '@angular/router';
import { PointDoc } from '../../../core/schema/point-schema';
import { ScoutKmlViewerModal } from '../../scout-kml-viewer/scout-kml-viewer-modal/scout-kml-viewer-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';


export function uniquePathNumberFn(scoutPathAndPaths : [ScoutPath,ScoutPath[]]): ValidatorFn {  
  return (control: AbstractControl): { [key: string]: any } | null => {
      const pathNumber = control.value
      const [scoutPath,scoutPaths] =  scoutPathAndPaths
      const bPathNumberExists = 
             pathNumber != null // allow undefined scout path???
          && pathNumber != scoutPath.pathNumber 
          && scoutPaths?.some(sp=>sp.pathNumber==pathNumber)
      return bPathNumberExists ? {pathNumberExists: control.value} : null;
  }
}
@Directive({
  selector: '[uniquePathNumber]',  
  providers: [{
      provide: NG_VALIDATORS, 
      useExisting: UniquePathNumberValidatorDirective,
      multi: true
  }]
})
export class UniquePathNumberValidatorDirective implements Validator { 
  @Input('uniquePathNumber') scoutPathAndPaths : [ScoutPath,ScoutPath[]];

  validate(control: AbstractControl): { [key: string]: any } | null { 
      return uniquePathNumberFn(this.scoutPathAndPaths)(control)
  }
}


@Component({
  selector: 'app-scout-path-editor',
  templateUrl: './scout-path-editor.component.html',
  styleUrls: ['./scout-path-editor.component.scss'],

})
export class ScoutPathEditorComponent extends FesBaseComponent implements OnInit, OnDestroy {

  @Input() scoutTaskDoc : ScoutTaskDoc
  @Input() scoutPathId : string
  @Input() public detailFn : (vi: ShowRequest) => void;
  @Input() set tabConfig( tabConf : AppTab ) { 
    this.initTabRemovalBehavior(tabConf) 
    this._tabConf = tabConf
    tabConf.setSelected = this.onTabSelected
  }
  _tabConf : AppTab

  @Output('contextJsonChange') get contextJsonChange() {
    return this.contextChangeEmitter
  }
  contextChangeEmitter = new EventEmitter<string>()
  
  @ViewChild('scoutPathPlotter') scoutPathPlotter : ScoutPathPlotterComponent
  @ViewChild('scoutSegmentsEditor') scoutSegmentsEditor : ScoutSegmentsEditorComponent
  @ViewChild('scoutPathComments') scoutPathComments : ScoutPathCommentsComponent
  @ViewChild('scoutPathAzimuth') scoutPathAzimuth : ScoutPathAzimuthComponent
  @ViewChild('scoutPathForm') scoutPathForm: NgForm;

  scoutPath : ScoutPath
  scoutPathIndex : number
  pathNumberEditValue : number
  floorEditValue : string
  descriptionEditValue : string
  scoutPaths: ScoutPath[] = []

  isLoadingBuildingPoints = true

  building : BuildingDoc
  haPoint : PointDoc
  buildingPoints : PointDoc[] = []
  devices: MapDevice[] = []
  errorMsg = ""
  isEditing = false
  uiLocked = false
  statusLocked = false
  overlayLoading = false
  bTabActive = false

  
  constructor(appState: AppState, 
      protected buildingService : BuildingService, 
      protected pointService : PointService,
      protected scoutTaskService : ScoutTaskService,
      protected antennaService : AntennaService,
      public roleMgr: RoleMgr,
      private router: Router,
      protected bsModalService: BsModalService
    ) { 
    super(appState)
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    super.destroy()
    this.scoutPathsSubscription?.unsubscribe()
    this.antennaDevicesSubscription?.unsubscribe()
  }

  scoutPathsSubscription: Subscription
  antennaDevicesSubscription: Subscription

  initScoutPath() {
    console.log('initScoutPath()')
    this.scoutPath = null
    this.pathNumberEditValue = null
    this.floorEditValue = null
    if ( !this.bIsEditingDescription ) {
      this.descriptionEditValue = null
    }
    this.scoutPaths = []
    this.scoutPathsSubscription?.unsubscribe()
    this.scoutPathsSubscription = this.scoutTaskDoc.get$('scoutPaths').subscribe( scoutPaths => {
      this.scoutPathsUpdated(scoutPaths)
    })
  }

  updatePathIndex() {
    this.scoutPathIndex = this.scoutPaths.findIndex(sp=>sp.id==this.scoutPathId)
  }


  async initialize() {
    this.unsubscribe()
    this.initScoutPath()
    this.subscribe(this.scoutTaskDoc.$, doc => {
      this.scoutTaskDocUpdated()
    })
    this.subscribe( this.scoutTaskDoc.get$('buildingId'), async buildingId => {
      try {

        this.building = await this.buildingService.rxCollection.findOne(buildingId).exec()
        this.scoutTaskDoc['buildingDoc'] = this.building

        this.isLoadingBuildingPoints = true
        this.buildingPoints = [] //await this.scoutTaskService.loadHaPointsInPath(this.scoutPath)
        this.isLoadingBuildingPoints = false

        if ( this.building ) {
          const buildingPointsQuery = this.pointService.rxCollection.find().where('buildingId').eq(this.building.id)
          this.subscribe( buildingPointsQuery.$, async points => {
            this.buildingPoints = points            
          })          
        } else {
          this.buildingService.publishError('Building ' + buildingId + ' not found',this)
        }

        if ( !this.scoutSegmentsEditor.isEditing || !this.scoutSegmentsEditor.isModified()) {
          this.scoutSegmentsEditor.scoutPathId = this.scoutPathId
          this.scoutSegmentsEditor.configure()
          this.initHaPointFromPath(this.scoutPath)
          this.plotWhenMapReady(this.scoutPath)
        } else if ( this.scoutSegmentsEditor.isModified() ) {
          console.warn('Scout Path Segments editor will lose edits if latest path is used')              
        }
      } catch ( error ) {
        console.log('Failed loading building or building points for scout task with buildingId=' + buildingId)
        this.buildingService.publishError(error,this)
      }
    })
  }


  scoutTaskDocUpdated(){

    if ( this.scoutSegmentsEditor?.isSaving 
      || this.scoutPathComments?.isSaving 
      || this.scoutPathAzimuth?.isSaving ) {
      console.log('ignoring scoutTaskDoc change because a child component is saving changes')
      return
    }

    if ( !this.isSavingPathNumberOrFloor && this.hasUnsavedData() ) {
      console.warn('scoutTaskDoc changed while edit in progress')
    }

    this.scoutPath = this.scoutPaths.find(sp=>sp.id==this.scoutPathId)
    if ( !this.scoutPath ) {
      this.errorMsg = "Invalid scout path (path id " + this.scoutPathId +")" 
    }
    this.statusHandler()
    this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
    //this.scoutPathIndex = this.getScoutPathIndexFromDoc()
    //this.selectedPathIndex = oldPathOrder !== false ? oldPathOrder : this.scoutPathIndex
    this.scoutSegmentsEditor?.configure()
    this.initHaPointFromPath(this.scoutPath)
    this.subscribeToAntennasForBuildingStageFloor() // Subscribe to antennas when path changes (building/stage/floor might have changed)
    this.scoutSegmentsEditor?.pathChanged() // notifies plotter to recenter and plot
  }

  somethingSaving() {
    return this.isSavingPathNumberOrFloor 
    || this.isSavingDescription 
    || this.scoutSegmentsEditor?.isSaving 
    || this.scoutPathComments?.isSaving 
    || this.scoutPathAzimuth?.isSaving 
  }

  scoutPathsUpdated(scoutPaths: ScoutPath[]) {
    console.log('scoutPathsUpdated()')

    ScoutPath.sortByPathNumber(scoutPaths)
    this.scoutPaths = scoutPaths
    this.scoutPath = this.scoutPaths.find(sp=>sp.id==this.scoutPathId)

    if ( this.scoutSegmentsEditor?.isSaving 
      || this.scoutPathComments?.isSaving 
      || this.scoutPathAzimuth?.isSaving ) {
      // keep form edits in Path Overall Info if another child component saves
      console.log('Child component is saving scout path changes so do not update form controls')
    } else {
      
      const nothingSaving = !this.somethingSaving()

      const updatePathAndFloor = () => {
        //console.log('updating form scout path path# and floor')
        this.pathNumberEditValue = this.scoutPath?.pathNumber
        this.floorEditValue = this.scoutPath?.floor
      }
      if ( this.isEditing && this.bPathOrFloorModified ) {
        if ( nothingSaving ) {
          this.appState.notifyWarning('Scout path changed while editing path# and/or floor (cancel to accept external change).',this,60)
        } else if ( this.isSavingPathNumberOrFloor ) {
          updatePathAndFloor()
        } else {
          console.log('Ignoring path update for path# and floor')
        }
      } else {
        updatePathAndFloor()
      }

      if ( this.scoutPathAzimuth?.isEditing && this.scoutPathAzimuth.isModified() ) {
        this.appState.notifyWarning('Scout path changed while editing azimuths (cancel to accept external change).',this,60)
      } else {
        console.log('updating form scout path azimuths')
        asyncScheduler.schedule( () => this.scoutPathAzimuth?.initFormValues(true) )
      }

      const updateDescription = () => {
        console.log('updating form scout path description')
        this.descriptionEditValue = this.scoutPath?.description
      }
      if ( this.bIsEditingDescription && this.bDescriptionModified ) {
        if ( nothingSaving ) {
          this.appState.notifyWarning('Scout path changed while editing description (cancel to accept external change).',this,60)
        } else if ( this.isSavingDescription ) {
          updateDescription()
        } else {
          console.log('Ignoring path update for description')
        }
      } else {
        updateDescription()
      }

    }
    this._tabConf.viewInfo.title.next(this._tabConf.viewInfo.titleFn())
    this.updatePathIndex()
    this.statusHandler()
    this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
  }


  isModified = () => this.bPathOrFloorModified || this.bDescriptionModified

  plotWhenMapReady(scoutPath:ScoutPath) {
    if ( this.scoutPathPlotter ) {
      this.scoutPathPlotter.plotScoutPathWhenReady(scoutPath)
    } else {
      timer(0).subscribe(_=>this.scoutPathPlotter?.plotScoutPathWhenReady(scoutPath))
    }
  }
  
  onTabSelected = ( bSelected : boolean ) => {
    //console.log('scout-path-editor tab selected: ' + bSelected )
    this.bTabActive = bSelected // prepare to show path plotter
    if ( bSelected ) { 
      const path = this.scoutPath
      timer(0).subscribe(_=>{
        if ( path == this.scoutPath ) {
          this.initialize()
        }
      })
    } else {
      this.unsubscribe()
    }
  }

  segmentsEdited( scoutPath : ScoutPath ) {
    this.initHaPointFromPath(this.scoutSegmentsEditor.scoutPath)
    this.scoutPathPlotter?.plotScoutPath(this.scoutSegmentsEditor.scoutPath)
  }

  initHaPointFromPath( scoutPath : ScoutPath ) {
    this.haPoint = null
    if ( scoutPath?.scoutPoints?.length ) {
      const haScoutPoint = scoutPath.scoutPoints.find(pt=>!pt.parentId&&pt.type=='HA')
      if ( !haScoutPoint ) {
        console.error('Could not find path start point (a point with no parent and type is HA)')
      } else {
        this.haPoint = this.buildingPoints.find(pt=>pt._id == haScoutPoint.pointId)
      }
    }
  }


  onOverlayEdited( overlay : MapImageOverlay | MapRouteGPXOverlay ) {
    if ( this.scoutPathPlotter ) {
      this.scoutPathPlotter.overlay = overlay as MapImageOverlay;
    }
  }


  onCancel() {
    this.isEditing = false
    this.scoutPathForm.form.markAsPristine()
    this.updateModifiedFlag()
    this.initScoutPath() 
  }


  bPathOrFloorModified : boolean = false
  

  updateModifiedFlag() {
    let bPathOrFloorModified = false
    if ( this.scoutPathForm?.dirty && this.scoutPath) {
      const bFloorChanged = this.scoutPath.floor != this.floorEditValue
      const bPathNumberChanged = this.scoutPath.pathNumber !=  this.pathNumberEditValue    
      bPathOrFloorModified = bFloorChanged || bPathNumberChanged
    }
    this.bPathOrFloorModified = bPathOrFloorModified
    return bPathOrFloorModified
  }


  bDescriptionModified = false

  updateDescriptionModifiedFlag() {
    this.bDescriptionModified = this.scoutPath.description != this.descriptionEditValue
  }

  isSavingPathNumberOrFloor = false

  async onSave() {
    console.log('onSave()')
    try {
      this.isSavingPathNumberOrFloor = true
      await this.scoutTaskDoc.atomicUpdate( (scoutTask) => {
        let path = scoutTask.scoutPaths.find(p=>p.id==this.scoutPath.id)
        if ( path ) {
          this.scoutTaskService.assignWhoAndWhenForPath(path)
          path.floor = this.floorEditValue
          path.pathNumber = this.pathNumberEditValue
        }      
        return scoutTask
      })
      this.onCancel()
    } catch (error) {
      console.error(`Save scout path #${this.scoutPath.pathNumber} failed: ${error?.message}`)
      this.scoutTaskService.publishError(error,this)
    } finally {
      this.isSavingPathNumberOrFloor = false
      this.scoutTaskService.syncNow()
    }
  }


  onReportDoc(evt:MouseEvent) {
    if ( evt.ctrlKey ) {  
      let url = '#' + this.router.createUrlTree(['scout', 'path-report', this.scoutTaskDoc._id, this.scoutPath.id],
      { queryParams: { wideMapZoomLevel: 19, detailMapZoomLevel: '', leafletMapToImageWaitSecs: 10, maxImageWidth: 1024, maxImageHeight: 1024 } }).toString()  
      window.open( url, 'ScoutPathReport-'+this.scoutPath.id );
    } else {
      this.detailFn( new ShowRequest('tab', 'scout report', this.scoutPath.id, this.scoutTaskDoc) )
    }
  }


  onReportData( evt : MouseEvent ) {
    this.detailFn( new ShowRequest('tab', 'scout data report', JSON.stringify({ pathIds: [this.scoutPath.id] }), this.scoutTaskDoc) )
  }

  onShowKml( evt : MouseEvent ) {
    ScoutKmlViewerModal.show(this.bsModalService,{ scoutTaskDoc: this.scoutTaskDoc, pathIds: [this.scoutPathId] })
  }

  hasUnsavedData = () => {
    return this.scoutSegmentsEditor?.isModified() 
    || this.scoutPathComments?.isModified()
    || this.scoutPathAzimuth?.isModified()
    || this.isEditing && this.bPathOrFloorModified 
    || this.bIsEditingDescription && this.bDescriptionModified
  }

  

  bIsEditingDescription = false
  onCancelDescriptionEdit() {
    this.bIsEditingDescription=false;
    this.descriptionEditValue = this.scoutPath.description
  }


  isSavingDescription = false

  async onSaveDescription() {
    console.log('onSaveDescription()')
    try {
      this.isSavingDescription = true
      await this.scoutTaskDoc.atomicUpdate( (taskDoc) => {
        let scoutPath = taskDoc.scoutPaths?.find(sp=>sp.id==this.scoutPathId)
        scoutPath.description = this.descriptionEditValue
        this.scoutTaskService.assignWhoAndWhenForPath(scoutPath)
        return taskDoc
      })
      this.bIsEditingDescription = false
    } catch ( error ) {
      this.scoutTaskService.publishError(error,this)
      return false
    } finally {
      this.isSavingDescription = false
      this.scoutTaskService.syncNow()
    }
  }

  private initTabRemovalBehavior( tabConfig : AppTab ) {
    tabConfig.hasUnsavedData = this.hasUnsavedData
    const defaultRemove = tabConfig.remove
    tabConfig.remove = (te:TabDirective) => { 
      if ( this.hasUnsavedData() ) {
        this.scoutSegmentsEditor.askDiscardEdits( ()=>defaultRemove(te) )
      } else if ( this.scoutPathComments?.isModified() ) {
        this.scoutPathComments.askDiscardEdits( ()=>defaultRemove(te) )
      } else if ( this.scoutPathAzimuth?.isModified() ) {
        this.scoutPathAzimuth.askDiscardEdits( ()=>defaultRemove(te) )
      } else {
        defaultRemove(te)
      }
    }
  }

  async onStatusChange(statusId: string){
    try {
      await this.scoutTaskDoc.atomicUpdate( (taskDoc) => {
        let scoutPath = taskDoc.scoutPaths?.find(sp=>sp.id==this.scoutPathId)
        scoutPath.statusId = parseInt(statusId)
        return taskDoc
      })
    } catch ( error ) {
      this.scoutTaskService.publishError(error,this)
      return false
    }
  }

  statusHandler(){
    if(this.scoutPath.statusId) {
      let status = this.scoutPath.statusId
      this.statusLocked = TaskStatus.statusLockHandler(status, this.roleMgr.canEditUsers)
      this.uiLocked = TaskStatus.uiLockHandler(status, this.roleMgr.canEditUsers)
    }
  }


  nextPath( pathIndexOffset: number ) {
    const currentPathIndex = this.scoutPaths.findIndex(sp=>sp.id==this.scoutPathId)
    const nextPathIndex = currentPathIndex + pathIndexOffset
    console.log(`nextPath(increment: ${pathIndexOffset}, nextIndex: ${nextPathIndex})`)
    if ( nextPathIndex >= 0 && nextPathIndex < this.scoutTaskDoc?.scoutPaths?.length) {
      const nextPath = this.scoutTaskDoc.scoutPaths[nextPathIndex]
      const changePath = () => {

        this.unsubscribe()
        this.onCancel()
        this.onCancelDescriptionEdit()
        this.scoutSegmentsEditor?.cancel(false)
        if ( this.scoutSegmentsEditor ) {
          this.scoutSegmentsEditor.scoutPath = null
          this.scoutSegmentsEditor.scoutPathId = null
        }
        if ( this.scoutPathPlotter ) {
          this.scoutPathPlotter.plot(null)
        }
        this.scoutPathComments?.onCancelComment()
        this.scoutPathAzimuth?.onCancel()
        //timer(0).subscribe(_=>{
          this.scoutPathIndex = nextPathIndex
          this.scoutPathId = nextPath.id    
          this.scoutPath = nextPath
          this._tabConf.viewInfo.id = this.scoutPathId
          this.initialize()
          this.contextChangeEmitter.next(this.scoutPathId)
        //})
        
      }

      if ( this.hasUnsavedData() ) {
        this.scoutSegmentsEditor.askDiscardEdits( changePath )
      } else if ( this.scoutPathComments?.isModified() ) {
        this.scoutPathComments.askDiscardEdits( changePath )
      } else if ( this.scoutPathAzimuth?.isModified() ) {
        this.scoutPathAzimuth.askDiscardEdits( changePath )
      } else {
        changePath()
      }
    }
  }

  private subscribeToAntennasForBuildingStageFloor() {
    // Unsubscribe from previous antenna subscription
    this.antennaDevicesSubscription?.unsubscribe()
    
    // Clear existing devices
    this.devices = [];
    
    if (!this.scoutTaskDoc?.buildingId || !this.scoutPath?.floor) {
      console.log('[ScoutPathEditor] Missing buildingId or floor for antenna subscription');
      return;
    }

    const pathNumber = this.scoutPath?.pathNumber;
    
    const params = {
      buildingId: this.scoutTaskDoc.buildingId,
      floorName: this.scoutPath.floor,
      stage: this.scoutTaskDoc.stage
    };

    try {
      const antennaObservable = this.antennaService.getAntennas$(params);
      
      this.antennaDevicesSubscription = antennaObservable.subscribe({
        next: (antennas) => {
          this.devices = antennas.map(antennaDoc => MapDevice.fromAntenna(antennaDoc));
          // console.log('[ScoutPathEditor] Antenna subscription received data:', antennas.map(antennaDoc =>antennaDoc.toJSON()));
          console.log(`[ScoutPathEditor] Updated devices from antenna subscription: ${antennas.length} antennas for path ${pathNumber}`, params);
        },
        error: (e: any) => {
          const msg = `Failed to get antennas for scout path ${pathNumber} ${JSON.stringify(params)}: ${e.message}`;
          console.error('[ScoutPathEditor] Antenna subscription error.', msg, e);
          this.antennaService.publishErrorObj(e, msg, this);
          this.devices = [];
        }
      });
      
    } catch (e: any) {
      const msg = `Failed to subscribe to antennas for scout path ${pathNumber} ${JSON.stringify(params)}: ${e.message}`;
      console.error('[ScoutPathEditor] Failed to create antenna subscription.', msg, e);
      this.antennaService.publishErrorObj(e, msg, this);
    }
  }
}
